import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, Video, XCircle } from "lucide-react";
import PixInfoCard from "../components/PixInfoCard";

/**
 * Página de retorno pós-Stripe. Faz polling em /api/payments/status até
 * confirmar pagamento (fallback ao próprio Stripe se webhook demorar).
 * Após confirmar, mostra link pra sala de vídeo.
 */
const BookingSuccess = () => {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, bookingId: null, error: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId) { setState(s => ({ ...s, loading: false, error: "Sem session_id" })); return; }
    let cancelled = false;
    const tick = async (attempts = 0) => {
      if (cancelled || attempts > 10) {
        setState(s => ({ ...s, loading: false, error: attempts > 10 ? "Tempo esgotado — verifique em Minhas consultas" : s.error }));
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, bookingId: data.booking_id, error: null, attempts });
          return;
        }
        setState(s => ({ ...s, attempts }));
        setTimeout(() => tick(attempts + 1), 2000);
      } catch (e) {
        setState({ loading: false, paid: false, bookingId: null, error: e?.response?.data?.detail || "Erro", attempts });
      }
    };
    tick(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="booking-success-page">
      {state.loading && (
        <>
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-emerald-700" />
          <h1 className="mt-4 font-display font-bold text-[#0B2B62] text-2xl">Confirmando pagamento…</h1>
          <p className="mt-2 text-slate-600">Aguarde — normalmente leva poucos segundos. Se pagou via PIX, a confirmação pode levar até 1 minuto.</p>
          <div className="mt-6 text-left"><PixInfoCard /></div>
        </>
      )}
      {!state.loading && state.paid && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-700" />
          <h1 className="mt-4 font-display font-bold text-[#0B2B62] text-3xl">Reserva confirmada</h1>
          <p className="mt-3 text-slate-600">Enviamos os detalhes por e-mail. Sua sala de vídeo estará disponível 15 min antes do horário — lembrete adicional chega 30 min antes.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {state.bookingId && (
              <Link to={`/consulta/${state.bookingId}`}
                data-testid="go-to-room"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold">
                <Video className="w-4 h-4" /> Ir para a sala de vídeo
              </Link>
            )}
            <Link to="/" className="inline-flex items-center px-5 py-3 rounded-md border border-slate-300 hover:bg-slate-50 text-sm font-semibold">Voltar ao início</Link>
          </div>
          <div className="mt-8 text-left"><PixInfoCard /></div>
        </>
      )}
      {!state.loading && !state.paid && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-red-500" />
          <h1 className="mt-4 font-display font-bold text-[#0B2B62] text-2xl">Ainda não confirmamos o pagamento</h1>
          <p className="mt-2 text-slate-600">{state.error || "Se o valor foi debitado, entre em contato com o suporte."}</p>
          <Link to="/profissionais" className="mt-6 inline-flex items-center px-5 py-3 rounded-md border border-slate-300 hover:bg-slate-50 text-sm font-semibold">Voltar à vitrine</Link>
          <div className="mt-8 text-left"><PixInfoCard /></div>
        </>
      )}
    </div>
  );
};

export default BookingSuccess;
